import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { recurring as api, customers as custApi, products as prodApi, apiError } from "@/lib/api";
import { computeTotals, money, dateInput } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";

export default function RecurringEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [custList, setCustList] = useState([]);
  const [prodList, setProdList] = useState([]);
  const [form, setForm] = useState({
    title: "", customer_id: "", frequency: "monthly",
    start_date: dateInput(new Date()), end_date: "", payment_terms: 30,
    line_items: [{ description: "", quantity: 1, unit_price: 0, vat_rate: 15, product_id: null }],
    discount: 0, discount_type: "amount", notes: "",
  });

  useEffect(() => {
    (async () => {
      const [cs, ps] = await Promise.all([custApi.list(), prodApi.list()]);
      setCustList(cs); setProdList(ps);
      if (editing) {
        const d = await api.get(id);
        setForm({
          title: d.title || "", customer_id: d.customer_id || "", frequency: d.frequency || "monthly",
          start_date: dateInput(d.start_date), end_date: dateInput(d.end_date), payment_terms: d.payment_terms || 30,
          line_items: d.line_items?.length ? d.line_items : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 15 }],
          discount: d.discount || 0, discount_type: d.discount_type || "amount", notes: d.notes || "",
        });
      }
      setReady(true);
    })().catch((e) => { toast.error(apiError(e)); setReady(true); });
  }, [id]);

  const totals = computeTotals(form.line_items, form.discount, form.discount_type);
  const setItem = (idx, key, val) => { const items = [...form.line_items]; items[idx] = { ...items[idx], [key]: key === "description" ? val : Number(val) }; setForm({ ...form, line_items: items }); };
  const pickProduct = (idx, pid) => { const p = prodList.find((x) => x.id === pid); const items = [...form.line_items]; if (p) items[idx] = { ...items[idx], product_id: p.id, description: p.name, unit_price: p.price, vat_rate: p.vat_rate }; setForm({ ...form, line_items: items }); };
  const addItem = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0, vat_rate: 15, product_id: null }] });
  const removeItem = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.customer_id) return toast.error("Please select a customer.");
    if (!form.line_items.some((li) => li.description)) return toast.error("Add at least one line item.");
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || null, line_items: form.line_items.filter((li) => li.description) };
      const d = editing ? await api.update(id, payload) : await api.create(payload);
      toast.success(`Recurring invoice ${editing ? "updated" : "created"}`);
      nav(`/recurring/${d.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!ready) return <Loading />;

  return (
    <div>
      <button onClick={() => nav("/recurring")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B] mb-4" data-testid="recurring-editor-back"><ArrowLeft className="w-4 h-4" /> Back to recurring</button>
      <PageHeader title={`${editing ? "Edit" : "New"} Recurring Invoice`} subtitle="This template auto-generates invoices on the schedule you set." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 space-y-4">
            <div><Label>Template Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monthly Hosting & Support" className="mt-1.5" data-testid="recurring-title" /></div>
            <div>
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="recurring-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{custList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{["weekly", "monthly", "quarterly", "yearly"].map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="recurring-start" /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" data-testid="recurring-end" /></div>
              <div><Label>Terms (days)</Label><Input type="number" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: Number(e.target.value) })} className="mt-1.5" data-testid="recurring-terms" /></div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-[#0B132B]">Line Items</h3>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="recurring-add-item"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
            </div>
            <div className="space-y-3">
              {form.line_items.map((li, idx) => (
                <div key={idx} className="bg-[#F1F5F9] rounded-lg p-3" data-testid={`recurring-line-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-5">
                      <Label className="text-xs">Description</Label>
                      {prodList.length > 0 && (
                        <Select value={li.product_id || ""} onValueChange={(v) => pickProduct(idx, v)}>
                          <SelectTrigger className="mt-1 h-8 text-xs bg-white"><SelectValue placeholder="Pick from catalogue" /></SelectTrigger>
                          <SelectContent>{prodList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Input value={li.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item description" className="mt-1 bg-white" data-testid={`recurring-desc-${idx}`} />
                    </div>
                    <div className="col-span-3 md:col-span-2"><Label className="text-xs">Qty</Label><Input type="number" step="0.01" value={li.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="mt-1 bg-white" /></div>
                    <div className="col-span-4 md:col-span-2"><Label className="text-xs">Unit Price</Label><Input type="number" step="0.01" value={li.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="mt-1 bg-white" /></div>
                    <div className="col-span-3 md:col-span-2"><Label className="text-xs">VAT %</Label><Input type="number" step="0.01" value={li.vat_rate} onChange={(e) => setItem(idx, "vat_rate", e.target.value)} className="mt-1 bg-white" /></div>
                    <div className="col-span-2 md:col-span-1 flex justify-end"><Button variant="ghost" size="icon" className="text-rose-500" onClick={() => removeItem(idx)} disabled={form.line_items.length === 1}><Trash2 className="w-4 h-4" /></Button></div>
                  </div>
                  <div className="text-right text-xs text-slate-500 mt-1">{money(Number(li.quantity || 0) * Number(li.unit_price || 0))}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5"><Label>Notes / Terms</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5" data-testid="recurring-notes" /></Card>
        </div>

        <div>
          <Card className="p-5 sticky top-20">
            <h3 className="font-display font-semibold text-[#0B132B] mb-4">Summary</h3>
            <div className="flex items-center gap-2">
              <Label className="text-xs w-24">Discount</Label>
              <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} className="h-9" data-testid="recurring-discount" />
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger className="h-9 w-20"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="amount">R</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{money(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">VAT</span><span>{money(totals.vat_amount)}</span></div>
              <div className="flex justify-between pt-2 border-t border-slate-100 text-base font-display font-bold text-[#0B132B]"><span>Per invoice</span><span data-testid="recurring-total">{money(totals.total)}</span></div>
            </div>
            <Button onClick={save} disabled={saving} className="w-full mt-5 bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-recurring-button"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : editing ? "Save Changes" : "Create Schedule"}</Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
